import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, PlugZap, Save, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  createTab,
  getTab,
  retestConnection,
  testConnection,
  updateTab,
  type TabAzureCredsIn,
  type TabCredsIn,
} from "@/api/tabs";
import { PasswordInput } from "@/components/PasswordInput";
import { Spinner } from "@/components/Spinner";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";

type FormState = {
  name: string;
  mssql_table_name: string;
  server: string;
  port: string;
  database: string;
  user: string;
  password: string;
  driver: string;
  trust_server_certificate: boolean;
  azure_connection_string: string;
  azure_container_name: string;
};

const INITIAL_FORM: FormState = {
  name: "",
  mssql_table_name: "GRS_GrievanceDetails",
  server: "",
  port: "",
  database: "",
  user: "",
  password: "",
  driver: "ODBC Driver 18 for SQL Server",
  trust_server_certificate: true,
  azure_connection_string: "",
  azure_container_name: "",
};

function toCreds(f: FormState): TabCredsIn {
  return {
    server: f.server.trim(),
    port: f.port ? Number(f.port) : null,
    database: f.database.trim(),
    user: f.user.trim(),
    password: f.password,
    driver: f.driver.trim() || null,
    trust_server_certificate: f.trust_server_certificate,
  };
}

function toAzureCreds(f: FormState): TabAzureCredsIn | null {
  const cs = f.azure_connection_string.trim();
  const container = f.azure_container_name.trim();
  if (!cs && !container) return null;
  return { connection_string: cs, container_name: container };
}

export function TabFormPage() {
  const params = useParams<{ id?: string }>();
  const editingId = params.id && params.id !== "new" ? params.id : null;
  const isEdit = !!editingId;
  const nav = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [testResult, setTestResult] = useState<
    | {
        ok: true;
        schema_ok: boolean;
        schema_report: Record<string, unknown>;
        azure_ok: boolean | null;
        azure_error: string | null;
      }
    | { ok: false; message: string }
    | null
  >(null);

  const existing = useQuery({
    queryKey: ["tabs", editingId],
    queryFn: () => getTab(editingId!),
    enabled: !!editingId,
  });

  useEffect(() => {
    if (existing.data) {
      const info = existing.data.mssql_conn_info ?? {};
      setForm((prev) => ({
        ...prev,
        name: existing.data.name,
        mssql_table_name: existing.data.mssql_table_name,
        // Pre-fill non-secret MSSQL fields. Password stays blank (rotation).
        server: info.server ?? "",
        port: info.port != null ? String(info.port) : "",
        database: info.database ?? "",
        user: info.user ?? "",
        driver: info.driver ?? prev.driver,
        trust_server_certificate:
          typeof info.trust_server_certificate === "boolean"
            ? info.trust_server_certificate
            : prev.trust_server_certificate,
        // Pre-fill container name so the operator sees what's saved. The
        // connection string stays blank – enter a new one to rotate.
        azure_container_name: existing.data.azure_container_name ?? "",
      }));
    }
  }, [existing.data]);

  const test = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        // Use the scoped endpoint so we can test with just Azure creds – the
        // backend falls back to the saved MSSQL password when we don't send
        // a new one.
        return retestConnection(editingId!, {
          creds: form.password ? toCreds(form) : undefined,
          mssql_table_name: form.mssql_table_name,
          azure_creds: toAzureCreds(form),
        });
      }
      return testConnection({
        creds: toCreds(form),
        mssql_table_name: form.mssql_table_name,
        azure_creds: toAzureCreds(form),
      });
    },
    onSuccess: (r) =>
      setTestResult({
        ok: true,
        schema_ok: r.schema_ok,
        schema_report: r.schema_report,
        azure_ok: r.azure_ok,
        azure_error: r.azure_error,
      }),
    onError: (e) =>
      setTestResult({
        ok: false,
        message: e instanceof ApiError ? e.message : "Connection failed.",
      }),
  });

  const save = useMutation({
    mutationFn: async () => {
      const azure = toAzureCreds(form);
      if (isEdit) {
        return updateTab(editingId!, {
          name: form.name,
          mssql_table_name: form.mssql_table_name,
          // Always send creds on edit — backend keeps the saved password
          // when the payload's password is blank, so admins can rotate
          // host/db/user without retyping the password each time.
          creds: toCreds(form),
          // Always send azure_creds on edit — the backend keeps the saved
          // connection_string / container_name when the payload's field is
          // blank, so admins can rotate one field without wiping the other.
          // `azure` is null only when the admin blanked both fields; in that
          // case leave the saved Azure creds alone (undefined) rather than
          // treating a blank form as a clear.
          azure_creds: azure ?? undefined,
        });
      }
      return createTab({
        name: form.name,
        mssql_table_name: form.mssql_table_name,
        creds: toCreds(form),
        azure_creds: azure,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? "Tab updated." : "Tab created.");
      void qc.invalidateQueries({ queryKey: ["tabs"] });
      nav("/admin/tabs");
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Save failed."),
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  if (isEdit && existing.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner label="Loading tab…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader
        title={isEdit ? "Edit tab connection" : "Connect a new exam DB"}
        backTo="/admin/tabs"
      />
      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col gap-4">
        <form
          className="card p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Tab name (e.g. SSC CGL 2026)">
            <input
              className="input-field"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
          </Field>

          <Field label="Grievance table name on the govt DB">
            <input
              className="input-field font-mono"
              value={form.mssql_table_name}
              onChange={(e) => set("mssql_table_name", e.target.value)}
              required
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Usually <code>GRS_GrievanceDetails</code>; override if the exam uses a different name.
            </p>
          </Field>

          <hr className="border-canvas-border dark:border-surface-border" />

          <p className="text-sm font-semibold">MSSQL connection</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Server (host or IP)">
              <input
                className="input-field font-mono"
                value={form.server}
                onChange={(e) => set("server", e.target.value)}
                required={!isEdit}
                placeholder="e.g. sql.exam.gov.in"
              />
            </Field>
            <Field label="Port (optional)">
              <input
                className="input-field font-mono"
                value={form.port}
                onChange={(e) => set("port", e.target.value)}
                placeholder="e.g. 1433"
              />
            </Field>
            <Field label="Database">
              <input
                className="input-field font-mono"
                value={form.database}
                onChange={(e) => set("database", e.target.value)}
                required={!isEdit}
              />
            </Field>
            <Field label="User">
              <input
                className="input-field font-mono"
                value={form.user}
                onChange={(e) => set("user", e.target.value)}
                required={!isEdit}
              />
            </Field>
            <Field
              label={isEdit ? "Password (leave blank to keep existing)" : "Password"}
              full
            >
              <PasswordInput
                className="font-mono"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required={!isEdit}
              />
            </Field>
            <Field label="Driver" full>
              <input
                className="input-field font-mono"
                value={form.driver}
                onChange={(e) => set("driver", e.target.value)}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Must match a driver installed on the API host. List with{" "}
                <code>odbcinst -q -d</code>.
              </p>
            </Field>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.trust_server_certificate}
                onChange={(e) => set("trust_server_certificate", e.target.checked)}
              />
              Trust server certificate
            </label>
          </div>

          <div className="alert-info text-xs">
            The govt DB user should have only <strong>SELECT</strong> on the grievance table
            and <strong>UPDATE</strong> on <code>RepliedGrievance, RepliedBy,
            RepliedDateTime, RepliedByIPAddress</code>. This app never INSERTs
            into or ALTERs the shared table.
          </div>

          <hr className="border-canvas-border dark:border-surface-border" />

          <div className="flex items-baseline justify-between">
            <p className="text-sm font-semibold">Azure Blob Storage (attachments)</p>
            <span className="text-xs text-slate-500 dark:text-slate-400">Optional</span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label={
                isEdit
                  ? "Connection string (leave blank to keep existing)"
                  : "Connection string"
              }
              full
            >
              <PasswordInput
                className="font-mono"
                value={form.azure_connection_string}
                onChange={(e) => set("azure_connection_string", e.target.value)}
                placeholder="e.g. DefaultEndpointsProtocol=https;AccountName=…;AccountKey=…;EndpointSuffix=core.windows.net"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Only needed if this exam's grievances have attachments stored in Azure Blob.
                Without it, admins won't be able to view or download the student's attached files.
              </p>
            </Field>
            <Field label="Container name" full>
              <input
                className="input-field font-mono"
                value={form.azure_container_name}
                onChange={(e) => set("azure_container_name", e.target.value)}
                placeholder="e.g. bp2025"
              />
            </Field>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => test.mutate()}
              disabled={test.isPending}
            >
              {test.isPending ? <Spinner label="Testing…" /> : <><PlugZap size={14} /> Test connection</>}
            </button>
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? <Spinner label="Saving…" /> : <><Save size={14} /> {isEdit ? "Save changes" : "Create tab"}</>}
            </button>
          </div>

          {testResult && "ok" in testResult && testResult.ok && (
            <>
              <ConnectionReport
                schemaOk={testResult.schema_ok}
                report={testResult.schema_report}
              />
              {testResult.azure_ok !== null && (
                <AzureReport
                  ok={testResult.azure_ok}
                  error={testResult.azure_error}
                />
              )}
            </>
          )}
          {testResult && "ok" in testResult && !testResult.ok && (
            <div className="alert-danger text-sm">{testResult.message}</div>
          )}
        </form>
      </main>
    </div>
  );
}

function Field({
  label,
  children,
  full = false,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      {children}
    </label>
  );
}

function ConnectionReport({
  schemaOk,
  report,
}: {
  schemaOk: boolean;
  report: Record<string, unknown>;
}) {
  const missing = (report.columns_missing as string[] | undefined) ?? [];
  const mismatched = (report.columns_mismatched as unknown[] | undefined) ?? [];
  const extras = (report.columns_extra as string[] | undefined) ?? [];
  return (
    <div className={schemaOk ? "alert-success" : "alert-warning"}>
      <p className="font-semibold inline-flex items-center gap-2 mb-2">
        {schemaOk ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
        {schemaOk ? "Connected. Schema matches." : "Connected, but schema issues:"}
      </p>
      {!schemaOk && (
        <ul className="text-xs list-disc pl-5 space-y-1">
          {missing.length > 0 && (
            <li>
              Missing columns: <code>{missing.join(", ")}</code>
            </li>
          )}
          {mismatched.length > 0 && (
            <li>{mismatched.length} column(s) mismatched in type or nullability.</li>
          )}
        </ul>
      )}
      {extras.length > 0 && (
        <p className="text-xs mt-2">
          Extra columns in this DB (ignored):{" "}
          <code>{extras.join(", ")}</code>
        </p>
      )}
    </div>
  );
}

function AzureReport({ ok, error }: { ok: boolean; error: string | null }) {
  return (
    <div className={ok ? "alert-success" : "alert-warning"}>
      <p className="font-semibold inline-flex items-center gap-2 mb-1">
        {ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
        {ok
          ? "Azure Blob container reachable."
          : "Could not reach the Azure Blob container."}
      </p>
      {!ok && error && (
        <p className="text-xs font-mono opacity-80 break-all">{error}</p>
      )}
    </div>
  );
}
